import React from "react";
import { Config } from "../config/config.js";
import { Helper } from "../util/helper.js";
import { HTTPService } from "../util/httpManager.js";
import { NotificationHelper } from "../util/notificationHelper.js";
import { ToolBar, ToolBarCustomButton, ToolBarStandardButtonType } from "../components/toolBar.js"
import { DropDownList } from "../components/dropDownList.js";
import { ComponentHelper } from "../util/componentHelper.js";
import * as monaco from 'monaco-editor';
import axios from "axios";
import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker&url';
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker&url';
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker&url';
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker&url';

self.MonacoEnvironment = {
    
    getWorker(_workerId: string, label: string): Worker {
        const base = Config.baseUrl.substring(0,Config.baseUrl.length-1)
        switch (label) {
            case 'typescript':
            case 'javascript': {
                return new Worker(
                   
                    new URL(
                        base + tsWorker,
                        import.meta.url,
                    ),
                    { type: 'module' },
                );
            }

            case 'json': {
                return new Worker(
                    new URL(
                        base + jsonWorker,
                        import.meta.url,
                    ),
                    { type: 'module' },
                );
            }

            default: {
                return new Worker(
                    new URL(base + editorWorker, import.meta.url),
                    { type: 'module' },
                );
            }
        }
    },
};

export interface ExtraLib {
    code: string
    filename: string
}

export interface TextEditorProps {
    name?: string
    onKeyPress?
    onTextChange?
    language?
    code?: string
    extraLibs?: ExtraLib[];
    readOnly?: boolean;
}

export class MonacoEditor extends React.Component<TextEditorProps, any> {
    private editor: monaco.editor.IStandaloneCodeEditor;
    private monacoDiv: HTMLDivElement;

    componentDidMount() {
        const readonly = Helper.hasValue(this.props.readOnly) && this.props.readOnly;
        const model = monaco.editor.createModel(this.props.code || '', this.props.language || 'javascript');
        this.editor = monaco.editor.create(this.monacoDiv, {
            model: model,
            automaticLayout: true,
            minimap: {
                enabled: false
            },
            readOnly: readonly
        });
        monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({ noSemanticValidation: true })
        if (Array.isArray(this.props.extraLibs)) {
            const co = monaco.languages.typescript.typescriptDefaults.getCompilerOptions();
            co.strictNullChecks = true;
            monaco.languages.typescript.typescriptDefaults.setCompilerOptions(co);
            monaco.languages.typescript.typescriptDefaults.setExtraLibs(this.props.extraLibs.map((el) => {
                return { content: el.code, filePath: el.filename }
            }));
        }

        this.editor.onDidBlurEditorText(async () => {
            if (this.props.code !== this.editor.getValue() && this.props.onTextChange) {
                this.props.onTextChange(this.editor.getValue());
            }
        });

        if (this.props.onKeyPress) {
            this.editor.onDidChangeModelContent(() => {
                this.props.onKeyPress(this.editor.getValue());
            });
        }
    }

    render() {
        const style = { height: '100%', width: "100%" };
        return (
            <div ref={(e) => this.monacoDiv = e} style={style} />
        );
    }
}

interface BaseProperties { type, visible, visibleCondition, required, model, name, readOnly, labelAndControlCombined, leftContainerStyle, defaultValue, rightContainerStyle, isKey, dc1 }

interface Schema<T extends BaseProperties> { isType?: string, properties: T };

function generateSchema(baseUri: string[], entity: any, metadata: any, isOverride: boolean): any[] {
    const entityKeys = Object.keys(entity);

    let data = entityKeys.filter(value => value != "IndividualFields")
        .map((ef) => { return "entity." + ef });

    if (entity.IndividualFields) {
        const individualFieldsData = Object.keys(entity.IndividualFields).map(ef => { return "entity.IndividualFields." + ef });
        data = data.concat(individualFieldsData);
    }
    data = data.sort((a, b) => { return a.localeCompare(b) });

    const modelNameEnum = data;

    const keys = Object.keys(metadata.refs);

    const fieldBaseSchema: Schema<BaseProperties> = {
        properties: {
            type: true,
            visible: { type: "boolean" },
            visibleCondition: { type: "string" },
            required: { type: "boolean" },
            model: { enum: modelNameEnum },
            name: { type: "string" },
            readOnly: { type: ["boolean", "string"] },
            labelAndControlCombined: { type: "boolean" },
            leftContainerStyle: { type: "object" },
            rightContainerStyle: { type: "object" },
            defaultValue: { type: "number" },
            isKey: { type: "" },
            dc1: { type: "object" }
        }
    }

    const textboxSchema: Schema<any> = {
        isType: "textbox",
        properties: {
            ...fieldBaseSchema.properties,
            maxLength: { type: "string" },
            isPassword: { type: "boolean" },
            style: { type: "string" },
            multiLine: { type: "boolean" }
        }
    }

    const kendoDropDownListSchema: Schema<any> = {
        isType: "kendo-drop-down-list",
        properties: {
            ...fieldBaseSchema.properties,
            optionLabel: { type: "string" },
            dataSource: { type: "string" },
            dataTextField: { type: "string" },
            dataValueField: { type: "string" },
            dataSourceUrl: { type: "string" },
            listWidth: { type: "number" },
            template: { type: "string" }
        }
    }

    const kendoDateTimePicker: Schema<any> = {
        isType: "kendo-date-time-picker",
        properties: {
            ...fieldBaseSchema.properties
        }
    }

    const kendoDatePicker: Schema<any> = {
        isType: "kendo-date-picker",
        properties: {
            ...fieldBaseSchema.properties
        }
    }

    const dynamic: Schema<any> = {
        isType: "dynamic",
        properties: {
            ...fieldBaseSchema.properties,
            moduleName: { type: "string" },
            multiLine: { type: "boolean" },
            props: { type: "object" }
        }
    }

    const kendoGrid: Schema<any> = {
        isType: "kendo_grid",
        properties: {
            ...fieldBaseSchema.properties,
            name: { type: "string" },
            data: { type: "object" }
        }
    }

    const isPanelbarDetails: Schema<any> = {
        isType: "is-panelbar-details",
        properties: {
            ...fieldBaseSchema.properties,
            template: { enum: ["AddressDetails", "SlaservicehoursDetails", "MachineDetails", "ArticleDetails", "ContactPersonDetails", "ProjectDetails", "ResourceDetails"] },
            url: { type: "string" }
        }
    }

    const isSearchField: Schema<any> = {
        isType: "is-search-field",
        properties: {
            ...fieldBaseSchema.properties,
            searchWindowUrlParameterName: { type: "string" },
            listWidth: { type: "number" },
            searchWindowUrlParameterModel: { type: "string" },
            autocompleteEnabled: { type: "boolean" },
            autocompleteTemplate: { type: "string" },
            autocompleteUrl: { type: "string" },
            autocompleteDataTextField: { type: "string" },
            autocompleteAdditionalFilterParameterName: { type: "string" },
            autocompleteAdditionalFilterParameter: { type: "string" },
            searchButtonEnabled: { type: "boolean" },
            searchWindowTitle: { type: "string" },
            searchWindowUrl: { type: "string" },
            searchWindowUrlAdditionalParameterName: { type: "string" },
            searchWindowUrlAdditionalParameterModel: { type: "string" },
            newButtonEnabled: { type: "boolean" },
            addNewUrl: { type: "string" },
            newObjectKey: { type: "string" },
            newObjectWindowTitle: { type: "string" },
            fullObjectModel: { type: "string" },
            fullObjectKey: { type: "string" },
            readOnlyTextField: { type: "string" },
            customButtonEnabled: { type: "boolean" },
            customButtonFunction: { type: "string" },
            customButtonTitle: { type: "string" },
            canOpenAdd: { type: "string" },
            addNewUrlParameters: { type: "object" }
        }
    }

    const statistic: Schema<any> = {
        isType: "statistic",
        properties: {
            ...fieldBaseSchema.properties,
            statisticId: { type: "string" },
            parameters: { type: "object" },
            title: { type: "string" }
        }
    }

    const tabWithModule: Schema<any> = {
        isType: "tab_with_module",
        properties: {
            type: { type: "string", isOverridable: false },
            title: { type: "string" },
            moduleName: { type: "string", isOverridable: false },
            visible: { type: ["string", "boolean"] },
            iconName: { type: "string" },
            helpUrl: { type: "string" },
            props: { type: "object" },
            isSaveable: { type: "boolean", isOverridable: false },
            component: { type: "string", isOverridable: false }
        }
    }

    const tabWithRegions: Schema<any> = {
        isType: "tab_with_regions",
        properties: {
            type: { type: "string", isOverridable: false },
            title: { type: "string" },
            visible: { type: ["string", "boolean"] },
            iconName: { type: "string" },
            helpUrl: { type: "string" },
            regions: {
                type: "array",
                uniqueItems: true,
                items: GetArrayPropWithRefNames(["region"])
            }
        }
    }

    const region: Schema<any> = {
        isType: "region",
        properties: {
            type: true,
            title: { type: "string" },
            visible: { type: ["string", "boolean"] },
            iconName: { type: "string" },
            size: { type: "array" },
            fields: { type: "array" },
            renderHeader: { type: "boolean" },
            breakStart: { type: "boolean" }
        }
    }

    const actionProps = {
        ...fieldBaseSchema.properties,
        onClick: { type: "string" },
        show: { type: "string" },
        floating: { type: "boolean" },
        floatingColor: { type: "string" },
        icon: { type: "string" },
        text: { type: "string" },
        data: { type: "object" }
    }

    const action_delete: Schema<any> = {
        isType: "delete",
        properties: actionProps
    }

    const action_follow: Schema<any> = {
        isType: "follow",
        properties: actionProps
    }

    const action_geomap: Schema<any> = {
        isType: "geomap",
        properties: actionProps
    }

    const action_save: Schema<any> = {
        isType: "save",
        properties: actionProps
    }

    const action_emailhistory: Schema<any> = {
        isType: "emailhistory",
        properties: actionProps
    }

    const action_email: Schema<any> = {
        isType: "email",
        properties: actionProps
    }

    const action_printdialog: Schema<any> = {
        isType: "printdialog",
        properties: actionProps
    }

    const action_qrreport: Schema<any> = {
        isType: "qrreport",
        properties: actionProps
    }

    const action_generic: Schema<any> = {
        isType: "action",
        properties: actionProps
    }

    const rightdrawerQuickinfo: Schema<any> = {
        isType: "rightdrawer_quickinfo",
        properties: {
            type: true,
            iconName: { type: "string" },
            moduleName: { type: "string" },
            props: { type: "object" }
        }
    }

    const rightdrawerHelp: Schema<any> = {
        isType: "rightdrawer_help",
        properties: {
            type: true,
            iconName: { type: "string" },
            moduleName: { type: "string" },
            renderAlways: { type: "boolean" },
            props: { type: "object" }
        }
    }

    const rightdrawerSearch: Schema<any> = {
        isType: "rightdrawer_search",
        properties: {
            type: true,
            iconName: { type: "string" },
            moduleName: { type: "string" },
            renderAlways: { type: "boolean" },
            props: { type: "object" }
        }
    }

    const kendoTemplate: Schema<any> = {
        isType: "kendoTemplate",
        properties: {
            type: true,
            name: { type: "string" },
            data: { type: "string" }
        }
    }

    const allRefs: Schema<any>[] = [kendoTemplate, rightdrawerSearch, rightdrawerHelp, rightdrawerQuickinfo, action_qrreport, action_printdialog, action_email, action_emailhistory, textboxSchema, kendoDropDownListSchema, kendoDateTimePicker, isPanelbarDetails, isSearchField, statistic, tabWithModule, tabWithRegions, region, kendoDatePicker, dynamic, kendoGrid, action_save, action_delete, action_save, action_generic, action_follow, action_geomap];

    const tabWithModuleSchema = {
        uri: "http://innosoft/tab-with-module-schema.json",
        schema: {
            type: "object",
            additionalProperties: false,
            properties: {
                "title": { type: "string" },
                "visible": { type: "string" },
                "iconName": { type: "string" },
                "helpUrl": { type: "string" }
            }
        }
    }
    const tabWithRegionsSchema = {
        uri: "http://innosoft/tab-with-regions-schema.json",
        schema: {
            type: "object",
            additionalProperties: false,
            properties: {
                "title": { type: "string" },
                "visible": { type: ["string", "boolean"] },
                "iconName": { type: "string" },
                "helpUrl": { type: "string" },
                "regions": {
                    type: "array",
                    uniqueItems: true,
                    items: { enum: ["ref:REGION_TITLE", "ref:REGION_BRAKE_START"] }
                }
            }
        }
    }
    const allFields = [];
    const allFieldsIndex = {};
    for (const x of allRefs) {
        const meta = {
            if: {
                properties: { "type": { "const": x.isType } },
                required: ["type"],
            },
            then: {
                additionalProperties: false,
                properties: x.properties
            }
        }
        allFieldsIndex[x.isType] = x;
        allFields.push(meta);
    }

    const customField = {
        uri: "http://innosoft/custom-field-schema.json",
        schema: {
            type: "object",
            properties: {
                "type": { enum: allRefs.map(x => x.isType) }
            },
            "allOf": allFields,
            "unevaluatedProperties": false
        }
    }
    const knownObjects = {};
    for (const m in metadata.refs) {
        const foundMetadata = allFieldsIndex[metadata.refs[m].type];

        if (foundMetadata) {
            const filtered = Object.keys(foundMetadata.properties)
                .filter(key => (foundMetadata.properties[key].isOverridable ?? true))
                .reduce((obj, key) => { obj[key] = foundMetadata.properties[key]; return obj; }, {});
            knownObjects[m] = { type: "object", properties: filtered, additionalProperties: false };
        }
        else {
            console.error("no metadata found");
        }
    }

    const baseAppSchema: any = {
        uri: 'http://myserver/foo-schema.json', // id of the first schema 
        fileMatch: baseUri, // associate with our model
        schema: {
            type: "object",
            required: ["formatVersion"],
            additionalProperties: false,
            properties: {
                formatVersion: {
                    enum: [2]
                },
                version: { type: "string" },
                rightDrawerContainer: {
                    type: "object",
                    properties: {
                        visible: { type: "boolean" },
                        width: { type: "number" },
                        drawerStyle: { type: "number" },
                        multipleComponents: { type: "array" }
                    },
                    additionalProperties: false,
                },
                menuDataSource: {
                    type: "array",
                    uniqueItems: true,
                    items: GetArrayPropWithRefNames(["printdialog", "save", "delete", "action", "follow", "geomap", "email", "emailhistory", "qrreport"]),
                },
                additional: { type: "object" },
                browserTitle: {
                    type: "string"
                },
                pageTitle: {
                    type: "string"
                },
                tabs: {
                    type: "array",
                    uniqueItems: true,
                    items: GetArrayPropWithRefNames(["tab_with_module", "tab_with_regions"])
                },
                refs: {
                    type: "object",
                    additionalProperties: false,
                    properties: knownObjects,
                    patternProperties: {
                        "^CUS_.*": customField.schema
                    }
                }
            }
        }
    };
    return [baseAppSchema, tabWithRegionsSchema, tabWithModuleSchema, customField];

    function GetRefNames(types: string[]) {
        const typeInRefs = keys.filter(x => types.includes(metadata.refs[x].type));
        const typeRefNames = typeInRefs.map(x => "ref:" + x);
        return typeRefNames;
    }

    function GetArrayPropWithRefNames(types: string[]) {
        const typeRefNames = GetRefNames(types);
        return {
            anyOf: [
                {
                    "enum": typeRefNames
                },
                {
                    "pattern": "^ref:CUS_.*"
                }
            ]
        };
    }
}
interface JSonEditorProps { entity: any, onChangeMetadata: (newMetadata: string) => void, metadataType: string };

interface MetadataForEditor { baseMetadata, jsonFiles, overridenMetadata, fsmVersion: string, metadataFolders: any[] };
export class MonacoJsonEditor extends React.Component<JSonEditorProps, { markers?: monaco.editor.IMarker[], metadata?: MetadataForEditor, selectedTab: number }> {
    private editor: monaco.editor.IStandaloneCodeEditor;
    private monacoDiv: HTMLDivElement;
    private debouncedUpdateModelDecorators: () => void;
    private baseModel: monaco.editor.ITextModel;
    private patchModel: monaco.editor.ITextModel;
    private modelUri;
    private patchUri;
    private selectedItem: { Id: string, Type: number, Description: string };
    private dropDownKey = kendo.guid();

    constructor(props) {
        super(props);
        this.debouncedUpdateModelDecorators = Helper.debounce(this.updateModelDecorators, 1000);
        this.state = { selectedTab: 0 };
    }

    updateModelDecorators = () => {
        const markers = monaco.editor.getModelMarkers({ resource: this.patchUri });
        this.setState({ markers });
    }

    async componentDidMount() {
        const result: MetadataForEditor = await HTTPService.get("Metadata/GetMetadataForEditor", { metaDataType: this.props.metadataType, isExternalUser: false });
        this.modelUri = monaco.Uri.parse('a://b/base.json');
        this.patchUri = monaco.Uri.parse('a://b/patch.json');
        this.baseModel = monaco.editor.createModel(result.baseMetadata, 'json', this.modelUri);
        this.patchModel = monaco.editor.createModel(result.overridenMetadata, 'json', this.patchUri);

        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
            validate: true,
            schemas: generateSchema([this.modelUri.toString(), this.patchUri.toString()], this.props.entity, JSON.parse(result.baseMetadata), true),
        });
        this.editor = monaco.editor.create(this.monacoDiv, {
            model: this.baseModel,
            automaticLayout: true,
            readOnly: true,
            minimap: { enabled: false }
        });

        this.editor.onDidChangeModelDecorations(() => {
            this.debouncedUpdateModelDecorators();
        })
        this.selectedItem = result.metadataFolders[0];
        this.setState({ metadata: result });

    }

    sync = async () => {
        const value = this.patchModel.getValue();
        const result = await HTTPService.post("Metadata/PatchMetadata", { baseMetadata: (this.state.metadata.baseMetadata), changeMetadata: value });
        this.props.onChangeMetadata(result.patched);
    }

    save = async () => {
        const value = this.patchModel.getValue();
        await this.SaveChanges(value);
        NotificationHelper.showSuccessMessage("Saved successfully");
    }

    delete = async () => {
        const value = "";
        await this.SaveChanges(value);
        this.patchModel.setValue(value);
        NotificationHelper.showSuccessMessage("Deleted successfully");
    }

    loadSelectedMetadata = async (dataItem?: any) => {
        this.selectedItem = dataItem;
        const result = await HTTPService.post("Metadata/GetMetadataFromCustomFolder", { metadataType: this.props.metadataType, metadataFolderType: this.selectedItem.Type, name: this.selectedItem.Description });
        if (dataItem?.Id) {
            this.patchModel.setValue(result.customMetadata);
            this.editor.setModel(this.patchModel); this.editor.updateOptions({ readOnly: false });
        }
    }

    changeTab = (tabId: number) => {
        if (tabId == 0) {
            this.editor.setModel(this.baseModel); this.editor.updateOptions({ readOnly: true });
        } else {
            this.editor.setModel(this.patchModel); this.editor.updateOptions({ readOnly: false });
        }
        this.setState({ selectedTab: tabId });
    }

    handleButtonClicked = (id) => {
        if (id == "save") {
            this.save();
        }
        else if (id == "sync") {
            this.sync();
        }
        else if (id == "delete") {
            this.delete();
        }
    }

    private async SaveChanges(value: string) {
        const result = await HTTPService.post("Metadata/SaveNewMetadata", { metadata: value, metadataType: this.props.metadataType, metadataFolderType: this.selectedItem.Type, name: this.selectedItem.Description });
        const meta = this.state.metadata;
        meta.metadataFolders = result.metadataFolders;
        this.dropDownKey = kendo.guid();
        this.setState({ metadata: meta });
    }

    render() {
        const cusBtn: Array<ToolBarCustomButton> = [];

        if (this.state.selectedTab == 1) {
            cusBtn.push({
                caption: "Save",
                enabled: true,
                iconClass: "fa-solid fa-check",
                id: "save"
            });

            cusBtn.push({
                caption: "Sync",
                enabled: true,
                iconClass: "fa-solid fa-rotate",
                id: "sync"
            });

            cusBtn.push({
                caption: "Delete",
                enabled: true,
                iconClass: "fa-solid fa-trash",
                id: "delete"
            });
        }
        let dropDown = null;
        const style = { width: "100%", flex: "1" };
        if (this.state?.metadata) {
            const template = '#const color = {"3":"lightgray","1":"lightgreen","2":"yellow"}; const type ={"-1":"","3":"User:","1":"Main group:","2":"Group:"}; # <div style="width:100%;display:inline-block;background-color:#=color[""+data.Type]#;">#=type[data.Type]# #=data.Description# #if(data.CustomizingExists){# <i class="fas fa-edit"></i></div>#}#';;
            const elemProps = ComponentHelper.createFactory().getDefaultDropDownProps();
            elemProps.field.dataTextField = "Description";
            elemProps.field.dataValueField = "Id";
            elemProps.field.template = template;
            elemProps.field.valueTemplate = '#=data.Description# #if(data.CustomizingExists){# <i class="fas fa-edit"></i></div>#}#';
            elemProps.field.optionLabel = null;
            elemProps.dataSource = this.state.metadata.metadataFolders;
            elemProps.value = this.selectedItem?.Id;
            elemProps.onChange = (field: { model: string }, value: any, dataItem?: any) => {
                this.loadSelectedMetadata(dataItem);
            }
            dropDown = <div style={{ width: "250px" }} key={this.dropDownKey}><DropDownList {...elemProps} /></div>;
        }
        return (<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <EditorTabControl selectedTab={this.state.selectedTab} onTabClicked={this.changeTab}></EditorTabControl>
            <div style={{ border: "1px solid gray", margin: "5px", padding: "5px", display: "flex", height: "100%", flexDirection: "column" }}>
                {this.state.selectedTab == 1 ? <div style={{ display: "flex", alignItems: "center" }}>
                    {dropDown}
                    <ToolBar customButtons={cusBtn} handleStandardButtonClick={() => { }} handleCustomButtonClick={this.handleButtonClicked} />
                </div> : null}
                <div ref={(e) => this.monacoDiv = e} style={style} />
                {this.state.selectedTab == 1 ? <Diagnostics diagnostics={this.state && this.state.markers} onJumpToRow={(rowNumber) => { this.editor.revealLine(rowNumber) }} /> : null}
            </div >
        </div>
        );
    }
}

class Diagnostics extends React.Component<{ diagnostics: monaco.editor.IMarker[], onJumpToRow: (rowNumber: number) => void }, any> {
    render() {
        return (<div>
            <ul style={{ marginLeft: "5px", height: "250px", overflow: "auto", borderTop: "1px solid" }}>{this.props.diagnostics && this.props.diagnostics.map((d, i) => {
                return <li key={i}
                    onClick={() => { this.props.onJumpToRow && this.props.onJumpToRow(d.startLineNumber) }}
                    style={{
                        borderColor: "red",
                        borderLeftWidth: "2px",
                        borderLeftStyle: "solid",
                        fontSize: "16px",
                        padding: "5px",
                        marginTop: "2px"
                    }}>{d.startLineNumber + ":" + (d.message.length > 200 ? d.message.substring(0, 300) + "..." : d.message)}</li>
            })}</ul>
        </div>
        )
    }
}

class EditorTabControl extends React.Component<{ selectedTab: number, onTabClicked?: (tabId: number) => void, children?}, any> {
    constructor(props) {
        super(props);
        this.state = {};
    }

    handleTabClick = (index, e) => {
        e.preventDefault();
        this.props.onTabClicked(index)
    }

    render() {
        const style = {
            border: "none",
            outline: "none",
            padding: "14px 16px",
            backgroundColor: "inherit"
        }

        const selectedStyle = { ...style };
        selectedStyle.backgroundColor = "#ccc";

        return (
            <div className="nav-tabs-custom">
                <button style={this.props.selectedTab == 0 ? selectedStyle : style} onClick={this.handleTabClick.bind(this, 0)} >Default</button>
                <button style={this.props.selectedTab == 1 ? selectedStyle : style} onClick={this.handleTabClick.bind(this, 1)} >Custom</button>
                <div className="tab-content" style={{ margin: "15px" }}>
                    {this.props.children}
                </div>
            </div>);

    }
};

export class MonacoEditorDiffViewer extends React.Component<{ original: { content: string, fileType: string }, modified: { content: string, fileType: string } }, any> {
    private editor: monaco.editor.IStandaloneDiffEditor;
    private monacoDiv: HTMLDivElement;

    componentDidMount() {
        const originalModel = monaco.editor.createModel(this.props.original.content, this.props.original.fileType)
        const modifiedModel = monaco.editor.createModel(this.props.modified.content, this.props.modified.fileType);

        this.editor = monaco.editor.createDiffEditor(this.monacoDiv);
        this.editor.setModel({
            original: originalModel,
            modified: modifiedModel
        });

    }

    render() {
        const style = { height: '100%', width: "100%" };
        return (
            <div ref={(e) => this.monacoDiv = e} style={style} />
        );
    }
}

export class ReactEditor extends React.Component<TextEditorProps, any> {
    private editor: monaco.editor.IStandaloneCodeEditor;
    private monacoDiv: HTMLDivElement;

    initMonaco = async () => {
        const reactTypings = (await axios.get(Config.baseUrl + "react_app/typeinfo/typescript/react.d.txt")).data;
        monaco.editor.getModels().forEach(model => model.dispose());
        const model = monaco.editor.createModel(this.props.code || '', 'typescript', monaco.Uri.parse("file:///main.tsx"));

        this.editor = monaco.editor.create(this.monacoDiv, {
            model: model,
            automaticLayout: true,
            minimap: { enabled: false },
        });
        //monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({ noSemanticValidation: true })
        if (Array.isArray(this.props.extraLibs)) {
            const co = monaco.languages.typescript.typescriptDefaults.getCompilerOptions();
            co.strictNullChecks = true;
            co.jsx = monaco.languages.typescript.JsxEmit.React;
            co.reactNamespace = "React";
            monaco.languages.typescript.typescriptDefaults.setCompilerOptions(co);
            monaco.languages.typescript.typescriptDefaults.setExtraLibs(this.props.extraLibs.map((el) => {
                return { content: el.code, filePath: el.filename }
            }));
        }

        monaco.languages.typescript.typescriptDefaults.addExtraLib(
            reactTypings,
            'file:///node_modules/@react/types/index.d.ts'
        );

        this.editor.onDidBlurEditorText(async () => {
            if (this.props.code !== this.editor.getValue() && this.props.onTextChange) {
                this.props.onTextChange(this.editor.getValue());
            }
        });

        if (this.props.onKeyPress) {
            this.editor.onDidChangeModelContent(() => {
                this.props.onKeyPress(this.editor.getValue());
            });
        }
    }

    componentDidMount() {
        this.initMonaco();

    }

    render() {
        const style = { height: '100%', width: "100%" };
        return (
            <div ref={(e) => this.monacoDiv = e} style={style} />
        );
    }
}